import { t } from "@wr/i18n";
import { Button, Dialog } from "@wr/ui";
import { useOverlay } from "../app/OverlayProvider";
import { useSeat } from "../app/SeatProvider";

export interface GuestLogoutDialogProps {
  onConfirm: () => void;
}

/**
 * **LOCKED (design spec §11): a guest gets a confirmation, a registered player
 * does not.**
 *
 * Logging out of a guest account destroys it and everything in it. The button
 * order is deliberate and is not a style choice:
 *
 *   primary  = "Create an account"   (→ the conversion sheet)
 *   danger   = "Log out anyway"
 *   ghost    = "Cancel"
 *
 * The destructive option is **not** the primary. This is simultaneously the
 * highest-value conversion moment in the product and the easiest way for a
 * player to lose their data, and both of those point the same direction.
 */
export function GuestLogoutDialog({ onConfirm }: GuestLogoutDialogProps) {
  const { overlay, close, openConversion } = useOverlay();
  const { seatHeld } = useSeat();

  return (
    <Dialog
      open={overlay === "guest-logout"}
      onClose={close}
      label={t("logout.guest.title")}
      testId="guest-logout-dialog"
    >
      <div className="wr-stack" style={{ gap: 16 }}>
        <h2 className="wr-display-sm">{t("logout.guest.title")}</h2>
        <p className="wr-body-md wr-ink-70">{t("logout.guest.warning")}</p>

        {/*
          §14.1 r5, LOCKED. The conversion offer loses its button while a seat
          is held and states why instead. Log out and Cancel both stay — this
          rule suppresses converting, not leaving.
        */}
        {seatHeld ? (
          <p className="wr-mono-sm wr-ink-70" data-testid="guest-logout-convert-blocked">
            {t("convert.blocked.in_game")}
          </p>
        ) : (
          <Button
            variant="primary"
            size="lg"
            fullWidth
            data-testid="guest-logout-convert"
            onClick={openConversion}
          >
            {t("logout.guest.create")}
          </Button>
        )}
        <Button
          variant="danger"
          size="md"
          fullWidth
          data-testid="guest-logout-confirm"
          onClick={() => {
            close();
            onConfirm();
          }}
        >
          {t("logout.guest.confirm")}
        </Button>
        <Button variant="ghost" size="md" fullWidth onClick={close}>
          {t("common.cancel")}
        </Button>
      </div>
    </Dialog>
  );
}
